import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DocIcon,
  Pagination,
  Button as PdButton,
  PlusIcon,
} from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";

import BaseStaffFilter from "~/pages/BaseStaff/BaseStaffFilter";
import BaseStaffBulkActions from "~/pages/BaseStaff/components/BaseStaffBulkActions";
import BaseStaffTableRowControls from "~/pages/BaseStaff/components/BaseStaffTableRowControls";
import Wrapper from "~/shared/ui/Wrapper";
import { EmployeeService } from "~/entity/Employee/api/service";
import { mergeEmployeeListWithGaps } from "~/entity/Employee/lib/restGaps";
import {
  EmployeeListOrder,
  EmployeeListOrderBy,
} from "~/entity/Employee/model/schema";
import { checkAccess } from "~/hooks/checkAccess";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { getFormatDate } from "~/libs/getFormatDate";
import {
  useGetEmployeeListSupplementQuery,
  useGetFileByHashLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useURLTableSort } from "~/shared/ui/TableV2";
import { UploadDataModal } from "~/widgets/UploadDataModal";

import {
  IBaseStaffUrlParams,
  parseBaseStaffFilterFromUrl,
} from "./model/baseStaffUrlParams";
import {
  mapBaseStaffFilterToQuery,
  mapBaseStaffFilterToReportQuery,
  mapBaseStaffFilterToSupplementQuery,
} from "./model/mapBaseStaffFilterToQuery";
import {
  baseStaffSortFieldForOrderBy,
  getBaseStaffColumns,
  getBaseStaffSortableApiKeys,
} from "./model/tableAdapter";
import { BASE_STAFF_OPTIONS } from "./options";

import s from "./BaseStaff.less";

const getEmployeePositionRowKey = (record: {
  employeePositionId?: string | number;
}) => String(record.employeePositionId ?? "");

const BaseStaff = () => {
  const { t, i18n } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [tableOption, setTableOption] = useState<string[]>(() =>
    BASE_STAFF_OPTIONS.filter((item) => item !== "verifiedInDnm"),
  );
  const setParams = useURLParamsSetter();
  const urlParams = useURLParamsGetter<IBaseStaffUrlParams>();
  const {
    hasPermission,
    hasSomePermission,
    loading: loadingPermissions,
    permissions,
  } = usePermissions();
  const history = useHistory();
  const {
    page,
    count = 25,
    openModal,
    searchText,
    sortField,
    sortType: sortTypeRaw,
  } = urlParams;
  const formFilter = useMemo(
    () => parseBaseStaffFilterFromUrl(urlParams),
    [urlParams],
  );

  const pageNumber = Number(page) || 0;
  const pageSize = Number(count) || 25;

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [page]);

  const access = checkAccess();
  const isFilter = openModal === "Filter";

  const { handleCount, handlePage } = usePaginationParams("urlParams");

  const handleCloseModal = () => {
    setParams({ openModal: undefined, currentId: undefined });
  };

  const hasDnmAccess = access.includes("dnm_access");

  useEffect(() => {
    const checkAccessDNM = hasPermission("employeeAccounting.employee.compare");
    setTableOption(
      checkAccessDNM
        ? BASE_STAFF_OPTIONS
        : BASE_STAFF_OPTIONS.filter((item) => item !== "verifiedInDnm"),
    );
  }, [hasPermission]);

  const orderByMappings: Record<string, EmployeeListOrderBy> = {
    fullname: "name",
    status: "status",
    createdAt: "createdAt",
    id: "id",
  };

  const REST_ORDER_BY: EmployeeListOrderBy[] = [
    "id",
    "name",
    "status",
    "createdAt",
  ];

  const sortableApiKeys = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getBaseStaffSortableApiKeys(tableOption, lang);
  }, [tableOption, i18n.language]);

  const { sortType: normalizedSortType, onSortChange } = useURLTableSort({
    sortField: sortField ? String(sortField) : undefined,
    sortType: sortTypeRaw ? String(sortTypeRaw) : undefined,
    sortableColumns: sortableApiKeys,
    setParams,
  });

  const tableSortField =
    sortField && sortableApiKeys.includes(String(sortField))
      ? String(sortField)
      : undefined;

  const orderBySource = tableSortField
    ? baseStaffSortFieldForOrderBy(tableSortField)
    : undefined;

  const resolvedOrderBy: EmployeeListOrderBy =
    orderBySource && orderByMappings[orderBySource]
      ? orderByMappings[orderBySource]
      : REST_ORDER_BY.includes(orderBySource as EmployeeListOrderBy)
        ? (orderBySource as EmployeeListOrderBy)
        : "id";

  const resolvedOrder: EmployeeListOrder =
    normalizedSortType === "DESC" ? "DESC" : "ASC";

  const legacySearchText =
    typeof searchText === "string" ? searchText : undefined;

  const queryVariables = useMemo(
    () =>
      mapBaseStaffFilterToQuery({
        filter: formFilter,
        searchText: legacySearchText,
        count: pageSize,
        page: pageNumber,
        resolvedOrderBy,
        resolvedOrder,
      }),
    [
      formFilter,
      legacySearchText,
      pageSize,
      pageNumber,
      resolvedOrderBy,
      resolvedOrder,
    ],
  );

  const supplementVariables = useMemo(
    () =>
      mapBaseStaffFilterToSupplementQuery({
        filter: formFilter,
        searchText: legacySearchText,
        count: pageSize,
        page: pageNumber,
        resolvedOrderBy,
        resolvedOrder,
      }),
    [
      formFilter,
      legacySearchText,
      pageSize,
      pageNumber,
      resolvedOrderBy,
      resolvedOrder,
    ],
  );

  const {
    data: listResponse,
    isLoading: loading,
    refetch: refetchList,
  } = useQuery(
    ["employeeList", queryVariables],
    () =>
      EmployeeService.GET.list(queryVariables).then(
        (response) => response.data,
      ),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const { data: supplement, refetch: refetchSupplement } =
    useGetEmployeeListSupplementQuery({
      variables: supplementVariables,
      fetchPolicy: "network-only",
    });

  const refetch = useCallback(() => {
    void refetchList();
    void refetchSupplement();
  }, [refetchList, refetchSupplement]);

  const columns = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getBaseStaffColumns(
      t,
      tableOption,
      refetch,
      tableSortField,
      normalizedSortType,
      lang,
    );
  }, [
    t,
    tableOption,
    refetch,
    tableSortField,
    normalizedSortType,
    i18n.language,
  ]);

  const baseStaffData = useMemo(
    () => mergeEmployeeListWithGaps(listResponse?.items || [], supplement),
    [listResponse, supplement],
  );

  const baseStaff = baseStaffData.map((row) => {
    const employee = row.employee;
    const employeePosition = row.employeePosition;
    const employeePositionId = employeePosition?.id;
    const employeesId = employee?.id;
    const positionsId = row.position?.id;
    const startDate = employeePosition?.startDate;
    const endDate = employeePosition?.endDate;
    const employeePositionInstanceId = employeePosition?.instanceId;
    const instanceId = row.direction?.instanceId || row.position?.directionId;
    const directionId = row.direction?.id;
    const roles = row.direction?.role;
    const positionNumber = employeePosition?.number;
    const verifiedInDnm = employee?.verifiedInDnm;
    const message = employeePosition?.rejectReason;
    const statusEdit =
      permissions[
        `employeeAccounting.employee.position.reject.${row.direction?.id}`
      ] ||
      permissions[
        `employeeAccounting.employee.position.verify.${row.direction?.id}`
      ];
    const {
      id,
      surname,
      name,
      middleName,
      lastEditDate,
      workPhone,
      createdAt,
      ladaAcademiaId,
    } = employee;

    const mainPosition = row.position
      ? `${row.position.code}. ${row.position.name}`
      : t("employee.haventMainPosition");

    const mainDealer =
      row.dealer?.dealerName ||
      row.organization?.name ||
      t("employee.haventMainDealer");

    const mainEmail = employeePosition?.email || "";

    const mainStatus = employeePosition
      ? t(`employee.${employeePosition.status}`)
      : t(`employee.fired`);

    return {
      id,
      employeePositionId,
      fullname: [surname, name, middleName].join(" "),
      workPhone,
      ladaAcademiaId,
      email: mainEmail,
      statusEmployee: mainStatus,
      status: mainStatus,
      rawPositionStatus: employeePosition?.status,
      verifiedInDnm,
      lastEditDate: getFormatDate(lastEditDate, true) || "",
      createdAt: getFormatDate(createdAt, true) || "",
      positions: mainPosition,
      dealerCenter: mainDealer,
      employeesId: employeesId != null ? String(employeesId) : "",
      positionsId: positionsId != null ? String(positionsId) : "",
      startDate,
      endDate,
      employeePositionInstanceId,
      instanceId,
      directionId,
      roles,
      positionNumber,
      message,
      statusEdit,
    };
  });

  const totalStaff = listResponse?.total ?? 0;

  const openAddModal = () => {
    history.push("add");
  };

  const handleViewDetailsStaff = (record: {
    id?: string | number;
    employeePositionId?: string | number;
  }) => {
    const positionQuery =
      record.employeePositionId != null
        ? `?employeePositionId=${encodeURIComponent(
            String(record.employeePositionId),
          )}`
        : "";
    history.push(`/base/viewing/${record.id}${positionQuery}`);
  };

  const handleRowEditStaff = (record: { id?: string | number }) => {
    history.push(`${record.id}`);
  };

  const toggleRowSelection = (rowKey: string, checked: boolean) => {
    setSelectedRowKeys((prev) =>
      checked ? [...prev, rowKey] : prev.filter((id) => id !== rowKey),
    );
  };

  const reportPollingRef = React.useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const stopReportPolling = () => {
    if (reportPollingRef.current) {
      clearInterval(reportPollingRef.current);
      reportPollingRef.current = null;
    }
  };

  const downloadEmployeeReport = async () => {
    const response = await EmployeeService.POST.getEmployeeReport(
      mapBaseStaffFilterToReportQuery({
        filter: formFilter,
        searchText,
      }),
    );
    const fileHash = response.data?.fileHash;
    if (fileHash) {
      stopReportPolling();
      reportPollingRef.current = setInterval(() => {
        getFileByHash({ variables: { hash: fileHash } });
      }, 2000);
    }
  };

  const [getFileByHash] = useGetFileByHashLazyQuery({
    fetchPolicy: "no-cache",
    async onCompleted(data) {
      const file = data?.FileGetter?.getFileByHash;
      if (file?.link) {
        stopReportPolling();
        const response = await fetch(file.link);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Отчет по сотрудникам.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    },
  });

  const handleBulkDownload = () => {
    void downloadEmployeeReport();
  };

  const handleBulkTransfer = () => {
    setParams({ openModal: "Transfer" });
  };

  const handleBulkDismiss = () => {
    setParams({ openModal: "Dismiss" });
  };

  const handleOpenUpload = () => {
    setParams({ openModal: "Upload" });
  };

  const clearSelection = () => {
    setSelectedRowKeys([]);
  };

  const hasSelectedRows = selectedRowKeys.length > 0;

  return (
    <Wrapper.Content
      title={t("ui.EmployeeBase")}
      filterComponent={<BaseStaffFilter />}
      controlComponent={
        <div className={s.headerActions}>
          {/* TODO PASDILER-2428: кнопки «Загрузить» и «Добавить» временно скрыты до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
          <PdButton
            color="primary"
            bold
            fontSize="12"
            size="medium"
            radius={5}
            icon={<DocIcon size={16} />}
            onClick={handleOpenUpload}
          >
            {t("ui.upload")}
          </PdButton>
          <PdButton
            color="primary"
            bold
            fontSize="12"
            size="medium"
            radius={5}
            icon={<PlusIcon size={16} fill="#ffffff" />}
            onClick={openAddModal}
          >
            {t("ui.add") || "Добавить"}
          </PdButton>
          */}
          <a
            className={s.hrefLink}
            href={"/userfiles/template2.docx"}
            download
            target={"_blank"}
            rel="noreferrer"
          >
            <PdButton
              color="secondary"
              bold
              fontSize="12"
              size="medium"
              radius={30}
              icon={<DocIcon size={16} fill="#495a64" />}
            >
              {"Шаблон перс. данных"}
            </PdButton>
          </a>
        </div>
      }
    >
      <BaseStaffBulkActions
        onDownload={handleBulkDownload}
        onTransfer={handleBulkTransfer}
        onDismiss={handleBulkDismiss}
        hasSelectedRows={hasSelectedRows}
      />

      <TableV2
        customRowsKey="employeePositionId"
        columns={columns}
        dataSource={baseStaff}
        loading={loading}
        rowActionsColumn={{
          width: 80,
          onCell: (record: { employeePositionId?: string | number }) =>
            selectedRowKeys.includes(getEmployeePositionRowKey(record))
              ? { className: "baseStaffRowSelectedCell" }
              : {},
        }}
        rowDecorator={(record: { employeePositionId?: string | number }) =>
          selectedRowKeys.includes(getEmployeePositionRowKey(record))
            ? { className: s.selectedRow }
            : {}
        }
        rowActions={(record: {
          employeePositionId?: string | number;
          rawPositionStatus?: string;
        }) => {
          const rowKey = getEmployeePositionRowKey(record);

          return (
            <BaseStaffTableRowControls
              record={record}
              rowKey={rowKey}
              selected={selectedRowKeys.includes(rowKey)}
              onToggle={toggleRowSelection}
              onViewDetails={handleViewDetailsStaff}
              onRowEdit={
                hasSomePermission([
                  "employeeAccounting.employee.card.edit.all",
                  "employeeAccounting.employee.card.edit.partial",
                ]) && record.rawPositionStatus !== "verified"
                  ? handleRowEditStaff
                  : undefined
              }
            />
          );
        }}
        actionsFixed={true}
        actionsPlacement="left"
        wrapperClassName={s.editor_table}
        sortableColumns={sortableApiKeys}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
      />
      <Pagination
        count={pageSize}
        page={pageNumber + 1}
        totalCount={Number(totalStaff) || 0}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <UploadDataModal
        open={openModal === "Upload"}
        onClose={handleCloseModal}
        types={["upload_employeeData"]}
      />
      {/* TODO PASDILER-2428: массовые модалки временно скрыты до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
      {openModal === "Transfer" && (
        <EmployeeTransferModal
          selectedCount={selectedRowKeys.length}
          onSuccess={clearSelection}
        />
      )}
      <EmployeeDismissModal
        selectedCount={selectedRowKeys.length}
        onSuccess={clearSelection}
      /> */}
    </Wrapper.Content>
  );
};

export default BaseStaff;
